import classNames from 'classnames';
import type { FunctionComponent } from 'react';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { omit } from 'underscore';

import { Analytics } from '@trello/atlassian-analytics';
import {
  firstPartyPluginsOrg,
  gmailId,
  gmailUrl,
  microsoftTeamsId,
  microsoftTeamsUrl,
} from '@trello/config';
import { LazyTrelloRenderer } from '@trello/editor/renderer';
import { Button } from '@trello/nachos/button';
import { BoardIcon } from '@trello/nachos/icons/board';
import { EmailIcon } from '@trello/nachos/icons/email';
import { InformationIcon } from '@trello/nachos/icons/information';
import { PowerUpIcon } from '@trello/nachos/icons/power-up';
import { RouterLink } from '@trello/router/router-link';
import type { PowerUpTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { makeSlug } from '@trello/urls';
import {
  useWorkspaceNavigation,
  useWorkspaceNavigationHidden,
} from '@trello/workspace-navigation';

import { Breadcrumbs } from 'app/src/components/Breadcrumbs';
import { LazyPowerUpsDeprecationBanner } from 'app/src/components/PowerUpsDeprecationBanner/LazyPowerUpsDeprecationBanner';
import { getMarketingScreenInfo } from 'app/src/getMarketingScreenInfo';
import type { Plugin } from './types';
import { useSharedStrings } from './useSharedStrings';

import styles from './ListingPage.less';

// eslint-disable-next-line @trello/assets-alongside-implementation
import compliant from 'resources/images/directory/icons/compliant.svg';
// eslint-disable-next-line @trello/assets-alongside-implementation
import defaultPowerUpIcon from 'resources/images/directory/icons/customIcon.png';
// eslint-disable-next-line @trello/assets-alongside-implementation
import notCompliant from 'resources/images/directory/icons/notCompliant.svg';

const getPluginTrackingName = (plugin: Plugin) => {
  // We only want to include public power-up names in
  // GAS events to avoid UGC violations for private power-ups
  const pluginName = plugin.listing ? plugin.listing.name : '';
  return plugin.public ? pluginName : undefined;
};

const SupportEmailComponent: FunctionComponent<{ plugin: Plugin }> = ({
  plugin,
}) => {
  if (!plugin.supportEmail) {
    return null;
  }

  let supportLink = plugin.supportEmail;

  const EMAIL_REGEX = /^[^@]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/i;

  if (EMAIL_REGEX.test(supportLink)) {
    supportLink = `mailto:${plugin.supportEmail}?subject=${encodeURIComponent(
      plugin.listing?.name ?? '',
    )}`;
  }

  return (
    <li className={styles.iconAndText}>
      <EmailIcon size="small" />
      <a
        className={styles.listingLink}
        target="_blank"
        rel="noopener noreferrer"
        href={supportLink}
      >
        <FormattedMessage
          id="templates.directory.contact-support"
          defaultMessage="Contact support"
          description="Contact support"
        />
      </a>
    </li>
  );
};

const AddPowerUpButton: FunctionComponent<{
  plugin: Plugin;
  isIntegration: boolean;
}> = ({ plugin, isIntegration }) => {
  let href = `/power-ups/${plugin.id}/enable`;
  let target = '';
  if (isIntegration) {
    target = '_blank';
    switch (plugin.id) {
      case microsoftTeamsId:
        href = microsoftTeamsUrl;
        break;
      case gmailId:
        href = gmailUrl;
        break;
      default:
        return null;
    }
  }
  return (
    <RouterLink
      onClick={() => {
        Analytics.sendClickedButtonEvent({
          buttonName: 'powerUpAddButton',
          source: 'publicDirectoryPowerUpDetailsScreen',
          attributes: {
            powerUpId: plugin.id,
            powerUpName: getPluginTrackingName(plugin),
            isPowerUpPublic: plugin.public,
            idOrganizationOwner: plugin.idOrganizationOwner,
            tags: plugin.tags,
          },
        });
      }}
      href={href}
      target={target}
      testId={getTestId<PowerUpTestIds>('pup-add-button-link')}
    >
      <Button
        appearance="primary"
        className={styles.addButton}
        data-testid={getTestId<PowerUpTestIds>('pup-add-button')}
      >
        {isIntegration ? (
          <FormattedMessage
            id="templates.directory.configure"
            defaultMessage="Configure"
            description="Configure"
          />
        ) : (
          <FormattedMessage
            id="templates.directory.add-power-up"
            defaultMessage="Add Power-Up"
            description="Add Power-Up"
          />
        )}
      </Button>
    </RouterLink>
  );
};

interface ListingPageProps {
  readonly plugin: Plugin;
}

export const ListingPage: FunctionComponent<ListingPageProps> = ({
  plugin,
}) => {
  const intl = useIntl();
  const { getCategoryMessage } = useSharedStrings();
  const [{ expanded: _workspaceNavigationExpanded }] = useWorkspaceNavigation();
  const [{ hidden: _workspaceNavigationHidden }] =
    useWorkspaceNavigationHidden();
  const workspaceNavigationExpanded =
    !_workspaceNavigationHidden && _workspaceNavigationExpanded;

  const categories = plugin.categories.map((category) => {
    const categoryMessage = getCategoryMessage(category);
    return (
      categoryMessage && (
        <RouterLink
          href={`/power-ups/category/${category}`}
          className={styles.tag}
          key={category}
          // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
          dangerouslySetInnerHTML={{
            __html: categoryMessage,
          }}
        />
      )
    );
  });

  const isTrelloOwned = plugin.idOrganizationOwner === firstPartyPluginsOrg;
  const isIntegration = plugin?.tags?.includes('integration');

  let message;
  let isCompliant;
  const pluginName = plugin.listing?.name ?? '';

  if (plugin.isCompliantWithPrivacyStandards === false) {
    message = (
      <FormattedMessage
        id="templates.directory_data_consent.power-up-does-not-comply-with-our-data-privacy-standards"
        defaultMessage="{pluginName} may store personal data and is not in compliance with <a>Trello's data and privacy practices</a>."
        description="Power-Up does not comply with our data privacy"
        values={{
          pluginName,
          a: (chunks) => (
            <a href="https://trello.com/privacy" target="_blank">
              {chunks}
            </a>
          ),
        }}
      />
    );
    isCompliant = false;
  } else {
    isCompliant = true;

    if (plugin.isCompliantWithPrivacyStandards === true) {
      message = (
        <FormattedMessage
          id="templates.directory_data_consent.power-up-stores-data-complies-with-our-data-privacy-standards"
          defaultMessage="{pluginName} stores personal data and is in compliance with <a>Trello's data and privacy practices</a>."
          description="Power-Up stores data and complies with our data privacy"
          values={{
            pluginName,
            a: (chunks) => (
              <a href="https://trello.com/privacy" target="_blank">
                {chunks}
              </a>
            ),
          }}
        />
      );
    } else {
      message = (
        <FormattedMessage
          id="templates.directory_data_consent.power-up-complies-with-our-data-privacy-standards"
          defaultMessage="{pluginName} is in compliance with <a>Trello's data and privacy practices</a>."
          description="Power-Up complies with our data privacy"
          values={{
            pluginName,
            a: (chunks) => (
              <a href="https://trello.com/privacy" target="_blank">
                {chunks}
              </a>
            ),
          }}
        />
      );
    }
  }

  const pluginDescription = plugin.listing ? plugin.listing.description : '';

  useEffect(() => {
    const { url, referrerUrl, referrerScreenName } = getMarketingScreenInfo();
    Analytics.sendMarketingScreenEvent({
      url,
      referrerUrl,
      referrerScreenName,
      event: {
        source: 'publicPowerUpDetailsScreen',
        name: 'publicPowerUpDetailsScreenV2',
        attributes: {
          powerUpId: plugin.id,
          powerUpName: getPluginTrackingName(plugin),
        },
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Breadcrumbs
        items={[
          {
            title: intl.formatMessage({
              id: 'templates.directory.power-ups',
              defaultMessage: 'Power-Ups',
              description: 'Power-Ups',
            }),
            href: '/power-ups',
          },
          {
            title: pluginName,
            href: `/power-ups/${plugin.id}/${makeSlug(pluginName)}`,
          },
        ]}
      />
      <div
        className={classNames(styles.listingRow, {
          [styles.workspaceNavigationExpanded]: workspaceNavigationExpanded,
        })}
      >
        <div
          className={classNames(styles.listingLeftColumn, {
            [styles.workspaceNavigationExpanded]: workspaceNavigationExpanded,
          })}
        >
          <div className={styles.iconContainer}>
            <img
              alt=""
              role="presentation"
              src={plugin.icon?.url ? plugin.icon.url : defaultPowerUpIcon}
              className={styles.pluginIcon}
            />
          </div>
          <div className={styles.sideBarSection}>
            <div
              className={styles.iconAndText}
              data-testid={getTestId<PowerUpTestIds>('pup-made-by')}
            >
              {isTrelloOwned ? (
                <BoardIcon size="small" />
              ) : (
                <PowerUpIcon size="small" />
              )}
              <span className={styles.madeBy}>
                <FormattedMessage
                  id="templates.directory.made-by"
                  defaultMessage="Made by {author}"
                  description="Made by author"
                  values={{ author: plugin.author }}
                />
              </span>
            </div>
            <div
              className={styles.badges}
              data-testid={getTestId<PowerUpTestIds>('pup-badges')}
            >
              {plugin.usageBrackets && plugin.usageBrackets.boards > 0 && (
                <div
                  className={styles.pupBadge}
                  title={intl.formatMessage(
                    {
                      id: 'templates.directory.usage-explanation',
                      defaultMessage: 'Enabled on more than {count} boards',
                      description: 'Used on number of boards',
                    },
                    {
                      count: plugin.usageBrackets.boards.toLocaleString(),
                    },
                  )}
                >
                  <BoardIcon
                    size="small"
                    dangerous_className={styles.usageBadgeIcon}
                  />
                  <span>{plugin.usageBrackets.boards.toLocaleString()} +</span>
                </div>
              )}
              {plugin.tags?.includes('dark-mode') && (
                <div
                  className={classNames(styles.pupBadge, styles.darkModeBadge)}
                >
                  <span>
                    <FormattedMessage
                      id="templates.directory_power_up_item.dark-mode"
                      // eslint-disable-next-line formatjs/no-emoji
                      defaultMessage="🌙 Supports dark mode"
                      description="Dark mode"
                    />
                  </span>
                </div>
              )}
              {plugin.tags?.includes('staff-pick') && (
                <div
                  className={classNames(styles.pupBadge, styles.genericBadge)}
                  title={intl.formatMessage({
                    id: 'templates.directory_power_up_item.staff-pick-explanation',
                    defaultMessage: 'A favorite of a Trello employee',
                    description: 'Staff pick explanation',
                  })}
                >
                  <span>
                    <FormattedMessage
                      id="templates.directory_power_up_item.staff-pick"
                      // eslint-disable-next-line formatjs/no-emoji
                      defaultMessage="❤️ Staff pick"
                      description="Staff pick"
                    />
                  </span>
                </div>
              )}
              {isIntegration && (
                <div
                  className={classNames(styles.pupBadge, styles.genericBadge)}
                  title={intl.formatMessage({
                    id: 'templates.directory_power_up_item.integration-explanation',
                    defaultMessage:
                      'This integration will not count towards your Power-Up limit',
                    description: 'Integration explanation',
                  })}
                >
                  <span>
                    <FormattedMessage
                      id="templates.directory_power_up_item.integration"
                      // eslint-disable-next-line formatjs/no-emoji
                      defaultMessage="🔗 Integration"
                      description="Integration"
                    />
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className={styles.sideBarSection}>
            <FormattedMessage
              tagName="h4"
              id="templates.directory.categories"
              defaultMessage="Categories"
              description="Categories"
            />
            {categories}
          </div>
          <div className={styles.sideBarSection}>
            <ul>
              <SupportEmailComponent plugin={plugin} />
              {plugin.privacyUrl && (
                <li className={styles.iconAndText}>
                  <InformationIcon size="small" />
                  <a
                    className={styles.listingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    href={plugin.privacyUrl}
                  >
                    <FormattedMessage
                      id="templates.directory.privacy-policy"
                      defaultMessage="Privacy policy"
                      description="Privacy policy"
                    />
                  </a>
                </li>
              )}
            </ul>
          </div>
          <div className={styles.sideBarSection}>
            <div className={styles.iconAndText}>
              <img
                className={styles.complianceIcon}
                src={isCompliant ? compliant : notCompliant}
                alt=""
                role="presentation"
              />
              <span className={styles.complianceText}>{message}</span>
            </div>
          </div>
        </div>
        <div
          className={classNames(styles.listingRightColumn, {
            [styles.workspaceNavigationExpanded]: workspaceNavigationExpanded,
          })}
        >
          <div className={styles.listingHeading}>
            <h1 className={styles.listingName}>{pluginName}</h1>
            <AddPowerUpButton plugin={plugin} isIntegration={isIntegration} />
          </div>
          {plugin.deprecation?.sunsetDate && (
            <LazyPowerUpsDeprecationBanner
              deprecatedPowerUps={[
                {
                  ...omit(plugin.deprecation, '__typename'),
                  sunsetDate: plugin.deprecation.sunsetDate,
                  name: pluginName,
                },
              ]}
              className={styles.deprecationBanner}
            />
          )}
          <div className={styles.listingContent}>
            <LazyTrelloRenderer
              text={pluginDescription}
              contentType="description"
            />
          </div>
        </div>
      </div>
    </>
  );
};
